import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const JOB_POLL_MS = 4000;
const MAX_JOB_POLLS = 150;
const OVERRIDE_MSG = { clear: "Override removed", include: "Requirement included per PM", strike: "Requirement struck per PM" };
const errMsg = (e, fallback) => e?.response?.data?.detail || e?.message || fallback;

// Optimistic local update after a checkbox decision; the server re-resolves provenance on reload.
const applyToggle = (reqs, r, include) => reqs.map((x) => (x.id === r.id ? { ...x, scope: { ...(x.scope || {}), state: include ? "included" : "excluded", source: "user" } } : x));

async function waitForJob(jobId) {
  for (let i = 0; i < MAX_JOB_POLLS; i++) {
    await new Promise((res) => setTimeout(res, JOB_POLL_MS));
    const { data: j } = await api.get(`/jobs/${jobId}`);
    if (j.status === "failed") throw new Error(j.error || "Summary failed");
    if (j.status === "done") return;
  }
}

// GET /requirements returns each item annotated with its resolved `scope` decision + excluded_by/kind (same logic as the report).
async function fetchRequirements(projectId) {
  const { data } = await api.get(`/projects/${projectId}/requirements`);
  return data;
}

// PM override note state (which row is being annotated + the note text).
function useOverrideNote() {
  const [noteFor, setNoteFor] = useState(null); // {id, action}
  const [note, setNote] = useState("");
  const reset = () => { setNoteFor(null); setNote(""); };
  return { noteFor, setNoteFor, note, setNote, reset };
}

// Requirements checklist data + actions (generate, cover toggle, PM override).
export function useRequirements(projectId) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const noteState = useOverrideNote();

  const load = useCallback(() => fetchRequirements(projectId).then(setData), [projectId]);
  useEffect(() => { load(); }, [load]);

  const generate = async (force) => {
    setBusy(true);
    try {
      const r = await api.post(`/projects/${projectId}/requirements?force=${force}`);
      if (r.status === 202) {
        toast.info("Reading the specification in the background — usually 1-2 minutes. You can keep working.");
        await waitForJob(r.data.job_id); await load();
        toast.success("Fabricator requirements ready");
      } else { setData(r.data); toast.success(`${r.data.requirements.length} fabricator requirements extracted`); }
    } catch (e) { toast.error(errMsg(e, "Summary failed")); } finally { setBusy(false); }
  };

  const reqs = data?.requirements || [];
  const toggle = async (r) => {
    const include = r.scope?.state !== "included";
    try {
      const { data: d } = await api.patch(`/projects/${projectId}/requirements/${r.id}`, { covered: include });
      setData({ ...data, requirements: applyToggle(reqs, r, include) });
      if (!include) toast.info("Marked as not carried — prints as excluded by estimator. Use “Reset to recommendation” to undo.");
      if (d.covered === d.total) toast.success("Every fabricator requirement is included in the bid");
      await load();
    } catch (e) { toast.error(errMsg(e, "Update failed")); }
  };
  const reset = async (r) => {
    try {
      await api.patch(`/projects/${projectId}/requirements/${r.id}`, { covered: null });
      toast.success("Back to the SteelForgeAI / playbook decision"); await load();
    } catch (e) { toast.error(errMsg(e, "Reset failed")); }
  };
  const override = async (r, action) => {
    if (action !== "clear" && !noteState.note.trim()) return toast.error("Add a short note for the override");
    try {
      await api.patch(`/projects/${projectId}/requirements/${r.id}/override`, { action, note: noteState.note });
      toast.success(OVERRIDE_MSG[action]); noteState.reset(); await load();
    } catch (e) { toast.error(errMsg(e, "Override failed")); }
  };
  const { noteFor, setNoteFor, note, setNote } = noteState;
  return { data, reqs, busy, generate, toggle, reset, override, noteFor, setNoteFor, note, setNote };
}
